import { cleanName } from "./names";

// What a household profile is, as far as the client and the server both need to agree. The
// drawings for each avatar live in components/avatar-icons.tsx.

export interface ProfileInfo {
  /** Absent only on the stand-in list used before the first load. */
  id?: string;
  name: string;
  avatar: AvatarKey;
  /** The household owner. Cannot be deleted. */
  owner: boolean;
  /** Passcodes are set outside the app for these, so their names are fixed. */
  nameLocked: boolean;
}

export const AVATAR_KEYS = [
  "bunny", "chicken", "cow", "pig", "sheep", "goat",
  "horse", "duck", "goose", "rooster", "donkey", "cat", "dog", "barn", "tractor",
] as const;

export type AvatarKey = (typeof AVATAR_KEYS)[number];

export const DEFAULT_AVATAR: AvatarKey = "barn";

export const isAvatarKey = (value: unknown): value is AvatarKey =>
  typeof value === "string" && (AVATAR_KEYS as readonly string[]).includes(value);

/** The picture a profile had before pictures could be chosen. */
export const defaultAvatarFor = (name: string): AvatarKey =>
  name === "Bun" ? "bunny" : name === "Chicken" ? "chicken" : DEFAULT_AVATAR;

/** The two profiles that sign in with a passcode held outside the database. */
export const PASSCODE_PROFILES = ["Bun", "Chicken"];

/** Profiles as they were before they could be added: used when a payload or cache predates them. */
export const LEGACY_PROFILES: ProfileInfo[] = [
  { name: "Bun", avatar: "bunny", owner: true, nameLocked: true },
  { name: "Chicken", avatar: "chicken", owner: false, nameLocked: true },
];

export const PROFILE_NAME_MAX = 24;
// "All" is the household filter and "Household" the shared budget owner; a profile with either name
// would be indistinguishable from them in a picker.
const RESERVED = ["all", "household"];

/** The tidied name, or the reason it cannot be one. */
export function checkProfileName(raw: unknown): { name: string; error?: undefined } | { error: string; name?: undefined } {
  const name = typeof raw === "string" ? cleanName(raw) : "";
  if (!name) return { error: "Enter a name." };
  if (name.length > PROFILE_NAME_MAX) return { error: `Use ${PROFILE_NAME_MAX} characters or fewer.` };
  if (!/^[\p{L}\p{N}][\p{L}\p{N} '’.-]*$/u.test(name)) return { error: "Use letters, numbers and spaces only." };
  if (RESERVED.includes(name.toLowerCase())) return { error: `“${name}” is reserved. Choose another name.` };
  return { name };
}
